// src/pages/part-labor-mapping/components/EditPartLaborsDialog.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { useAddLaborToPartMutation, useDeleteLaborToPartMutation } from "../../../../api/endpoints/partLaborMapping";
import { useLanguage } from "../../../../contexts/LanguageContext";

const norm = (s) => (s ?? "").toString().trim().toUpperCase();

const EditPartLaborsDialog = ({ visible, onHide, partRow, labors, onSaved }) => {
  const { th } = useLanguage();
  const toast = useRef(null);

  // PartCode normalize
  const partCode =
    partRow?.PartCode ?? partRow?.partCode ?? partRow?.Matnr ?? partRow?.matnr ?? "-";

  // LaborMappings normalize
  const laborSrc = partRow?.LaborMappings ?? partRow?.laborMappings ?? [];

  const initialMappings = useMemo(
    () =>
      (laborSrc || []).map((m) => ({
        id: m.Id ?? m.id,
        code: m.LaborCode ?? m.laborCode ?? m.Code ?? m.code,
        name: m.LaborName ?? m.laborName ?? m.Name ?? m.name ?? m.Text ?? m.text,
        isNew: false,
      })),
    [laborSrc]
  );

  const [mappingList, setMappingList] = useState(initialMappings);
  const [selectedLaborId, setSelectedLaborId] = useState(null);

  useEffect(() => {
    if (visible) {
      setMappingList(initialMappings);
      setSelectedLaborId(null);
    }
  }, [visible, initialMappings]);

  const [addLaborToPart, { isLoading: isSaving }] = useAddLaborToPartMutation();
  const [deleteLaborToPart, { isLoading: isDeleting }] = useDeleteLaborToPartMutation();

  // Labor options normalize
  const laborOptions = useMemo(
    () =>
      (labors || []).map((l) => {
        const id = l.Id ?? l.id;
        const code = l.Code ?? l.code;
        const text = l.Text ?? l.text;
        return { label: `${code ?? ""} — ${text ?? ""}`, value: id, code, text };
      }),
    [labors]
  );

  const onAddSelectedLabor = () => {
    if (!selectedLaborId) return;
    const opt = laborOptions.find((o) => o.value === selectedLaborId);
    if (!opt || !opt.code) return;

    // Aynı laborCode varsa ekleme (case/whitespace insensitive)
    const exists = mappingList.some((m) => norm(m.code) === norm(opt.code));
    if (exists) {
      toast.current?.show({
        severity: "warn",
        summary: th("common.warning"),
        detail: th("plm.alreadyExists") || "Bu işçilik zaten ekli.",
        life: 2500,
      });
      return;
    }

    setMappingList((prev) => [
      ...prev,
      { id: undefined, code: opt.code, name: opt.text, isNew: true },
    ]);
    setSelectedLaborId(null);
  };

  const onRemoveRow = async (row) => {
    if (row.id) {
      try {
        await deleteLaborToPart({ id: row.id }).unwrap();
        setMappingList((prev) => prev.filter((x) => norm(x.code) !== norm(row.code)));
        toast.current?.show({
          severity: "success",
          summary: th("common.success"),
          detail: th("plm.deleted") || "Silindi.",
          life: 2000,
        });
        // Listeyi de yenilet (ilk sayfaya dön/refetch)
        onSaved?.();
      } catch {
        toast.current?.show({
          severity: "error",
          summary: th("common.error"),
          detail: th("plm.deleteError") || "Silme sırasında hata oluştu.",
          life: 3500,
        });
      }
    } else {
      // id yoksa yerelde sil
      setMappingList((prev) => prev.filter((x) => norm(x.code) !== norm(row.code)));
      // Yerel silmede parent yenilemeye gerek yok, istersen aç:
      // onSaved?.();
    }
  };

  const onSaveAll = async () => {
    const codes = mappingList.map((m) => m.code).filter(Boolean);
    try {
      await addLaborToPart({
        partCode: partCode,
        laborCodes: codes,
      }).unwrap();

      toast.current?.show({
        severity: "success",
        summary: th("common.success"),
        detail: th("plm.saved") || "Kaydedildi.",
        life: 2000,
      });

      onSaved?.(); // ← kaydetten sonra da yenile
      onHide?.();
    } catch {
      toast.current?.show({
        severity: "error",
        summary: th("common.error"),
        detail: th("plm.saveError") || "Kaydetme sırasında hata oluştu.",
        life: 3500,
      });
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label={th("common.cancel")}
        icon="pi pi-times"
        className="p-button-outlined"
        onClick={onHide}
        disabled={isSaving || isDeleting}
      />
      <Button
        label={th("common.save")}
        icon="pi pi-check"
        onClick={onSaveAll}
        loading={isSaving}
      />
    </div>
  );

  const actionTemplate = (row) => (
    <Button
      icon="pi pi-trash"
      severity="danger"
      outlined
      onClick={() => onRemoveRow(row)}
      loading={isDeleting}
    />
  );

  return (
    <Dialog
      header={`${th("parts.jobsForPart")}: ${partCode}`}
      visible={visible}
      onHide={onHide}
      modal
      style={{ width: "56rem" }}
      footer={footer}
    >
      <Toast ref={toast} />

      <div className="flex gap-2 align-items-end mb-3">
        <div className="w-full">
          <label className="block mb-2">{th("warrantyClaims.detail.labor.laborSelectPlaceholder")}</label>
          <Dropdown
            value={selectedLaborId}
            onChange={(e) => setSelectedLaborId(e.value)}
            options={laborOptions}
            placeholder={th("labor.laborSelectPlaceholder")}
            showClear
            filter
            className="w-full"
          />
        </div>
        <Button label={th("common.add")} icon="pi pi-plus" onClick={onAddSelectedLabor} />
      </div>

      <DataTable value={mappingList} showGridlines stripedRows responsiveLayout="scroll">
        <Column field="code" header={th("laborCatalog.table.laborCode")} style={{ width: "20%" }} />
        <Column field="name" header={th("laborCatalog.table.description") } />
        <Column
          header={th("laborCatalog.table.actions")}
          body={actionTemplate}
          style={{ width: 120, textAlign: "center" }}
        />
      </DataTable>
    </Dialog>
  );
};

export default EditPartLaborsDialog;
